import { format, parseISO } from 'date-fns';

import type { SelectOption } from '@/components';
import type { MutationResponse } from '@/types/api/mutations';
import type {
  BookingClient,
  CatalogServiceCustomField,
  ClientListItem,
} from '@/types/api/queries';

import { DISPLAY_DATES_FORMAT } from '@/constants/dates';

import { DisplayToast } from './displayToast';

type ClientsSearchResponse = { data: ClientListItem[] };

// Server timestamps carry the gym's own offset (…T14:00:00.000+03:00), and the board is that gym's
// wall clock: the write side sends a bare local timestamp for the backend to read in its zone. So the
// time of day is taken from the string itself — routing it through `Date` would re-render it in the
// viewer's timezone and slide every card for an admin sitting in another one.
const WALL_CLOCK_PATTERN = /T(\d{2}):(\d{2})/;

export const minutesFromMidnight = (isoTime: string) => {
  const wallClock = WALL_CLOCK_PATTERN.exec(isoTime);

  if (!wallClock) {
    return 0;
  }

  return Number(wallClock[1]) * 60 + Number(wallClock[2]);
};

export const isSlotOffered = (
  slots: { starts_at: string }[],
  isoTime: string,
) => {
  const minute = minutesFromMidnight(isoTime);

  return slots.some((slot) => minutesFromMidnight(slot.starts_at) === minute);
};

export const requiredFieldIds = (fields: CatalogServiceCustomField[]) =>
  fields.filter((field) => field.required).map((field) => field.id);

export const minutesToLabel = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

export const bookingTime = (isoTime: string) =>
  minutesToLabel(minutesFromMidnight(isoTime));

export const bookingDateTime = (isoTime: string) =>
  `${format(parseISO(isoTime), DISPLAY_DATES_FORMAT)} ${bookingTime(isoTime)}`;

export const bookingTimeRange = (startsAt: string, endsAt: string) =>
  `${bookingTime(startsAt)}–${bookingTime(endsAt)}`;

export const bookingDateTimeRange = (startsAt: string, endsAt: string) =>
  `${bookingDateTime(startsAt)}–${bookingTime(endsAt)}`;

const START_STEP_MINUTES = 30;

export const dayStartOptions = (): SelectOption[] =>
  Array.from({ length: (24 * 60) / START_STEP_MINUTES }, (_, index) => ({
    id: index * START_STEP_MINUTES,
    name: minutesToLabel(index * START_STEP_MINUTES),
  }));

export const bookingPersonName = (
  client: BookingClient,
  guestInfo: string | null,
  guestFallback: string,
) => {
  if (client) {
    return client.full_name;
  }
  return guestInfo?.trim() || guestFallback;
};

export const transformClientsResponse = (response: unknown) =>
  (response as ClientsSearchResponse).data;

export const clientOptionLabel = (option: unknown) => {
  const client = option as ClientListItem;

  return `${client.first_name} ${client.last_name}`;
};

export const clientOptionValue = (option: unknown) =>
  String((option as ClientListItem).id);

export const notifyBookingSuccess = (response?: MutationResponse) => {
  if (response?.flash_message) {
    DisplayToast.success(response.flash_message);
  }
};
